// imports
import { Fab, FabProps } from '@mui/material';
import { styled } from '@mui/material/styles';
import { FOCUS_BLUE, TEXT_GRAY } from '../constants/colors';

/**
 * CustomButton
 *
 * Renders a generic button.
 * The purpose of this component is to ensure consistent styling across the buttons overlaid on the map.
 * Can be customized for different sizes and variants.
 *
 * @returns {JSX.Element} The rendered custom map controls component.
 */

// Grouped variant means that the group of buttons are stacked vertically, with rounded outer corners
// Standalone variant means that it is a single button of custom size
interface CustomButtonProps extends Omit<FabProps, 'size' | 'variant'> {
  width?: string | number;
  height?: string | number;
  iconSize?: string | number;
  buttonVariant?: 'grouped' | 'standalone';
}

const StyledFab = styled(Fab, {
  shouldForwardProp: (prop) =>
    !['customWidth', 'customHeight', 'iconSize', 'buttonVariant'].includes(
      prop as string
    ),
})<{
  customWidth?: string | number;
  customHeight?: string | number;
  iconSize?: string | number;
  buttonVariant?: 'grouped' | 'standalone';
}>(
  ({
    customWidth = '2.5rem',
    customHeight = '2.5rem',
    iconSize = '1.56rem',
    buttonVariant = 'grouped',
  }) => ({
    width: customWidth,
    height: customHeight,
    minHeight: customHeight,
    opacity: 0.9,
    backgroundColor: 'white',
    border: '2px solid #c0c0c0ff',
    borderRadius: buttonVariant === 'standalone' ? '4px' : 0,
    margin: 0,
    position: 'relative',
    zIndex: 3,
    '&:hover': {
      backgroundColor: '#f5f5f5',
      border: '2px solid #c0c0c0ff',
      boxShadow: 'none',
    },
    // When button is in focus, the zIndex is increased to elevate the outline above the surrounding buttons
    '&:focus, &.Mui-focusVisible': {
      zIndex: 4,
      outline: `2px solid ${FOCUS_BLUE}`,
      outlineOffset: '0px',
      backgroundColor: '#f5f5f5',
      border: '2px solid #c0c0c0ff',
      boxShadow: 'none',
    },
    '&:active': {
      outline: 'none',
      backgroundColor: '#e5e5e5',
      border: '2px solid #c0c0c0ff',
      boxShadow: 'none',
    },
    ...(buttonVariant === 'grouped' && {
      '&:first-of-type': {
        borderRadius: '4px 4px 0 0',
      },
      '&:last-of-type': {
        borderRadius: '0 0 4px 4px',
      },
      '& + &': {
        borderTop: 'none',
      },
    }),
    '& .MuiSvgIcon-root': {
      fontSize: iconSize,
      color: TEXT_GRAY,
    },
    '&.Mui-disabled': {
      backgroundColor: '#e0e0e0',
      border: '2px solid #c0c0c0ff',
      opacity: 0.9,
      '& .MuiSvgIcon-root': {
        color: '#9e9e9e',
      },
    },
  })
);

function CustomButton({
  width,
  height,
  iconSize,
  buttonVariant = 'grouped',
  children,
  ...restProps
}: CustomButtonProps) {
  return (
    <StyledFab
      customWidth={width}
      customHeight={height}
      iconSize={iconSize}
      buttonVariant={buttonVariant}
      {...restProps}
    >
      {children}
    </StyledFab>
  );
}

export default CustomButton;
